/**
 * UpdateNotifier — app-wide "a new version is available" prompt.
 *
 * Rendered once at the app root. When useVersionCheck detects that a newer
 * build has been deployed, it puts the choice in the middle of the screen,
 * over the page: a corner snackbar was easy to ignore, and people kept
 * working on yesterday's build for days. The page stays visible behind the
 * dimmed backdrop, so it is plainly "your work is still here, but read this".
 *
 * We never auto-reload: a person may have unsaved work (filters, a half-typed
 * question), so the choice stays theirs — Later keeps this tab as it is until
 * the next deploy.
 *
 * Reload uses window.location.reload(), which re-fetches index.html (served
 * no-cache) → the new content-hashed bundles → the new UI. No "hard" reload is
 * needed because assets are content-hashed.
 */
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import RocketLaunchOutlined from '@mui/icons-material/RocketLaunchOutlined';
import { Modal } from '../ui-kit';
import { useVersionCheck } from '../hooks/useVersionCheck';

export default function UpdateNotifier() {
  const updateAvailable = useVersionCheck();
  const [dismissed, setDismissed] = React.useState(false);

  return (
    <Modal
      open={updateAvailable && !dismissed}
      // Later is the way out, not a click on the page behind: the whole point
      // is that this is not brushed aside without a decision.
      dismissible={false}
      // The page stays visible behind, but blurred: plainly still there, and
      // plainly not where the next click goes.
      backdropBlur
      onClose={() => setDismissed(true)}
      size="sm"
      title="A new app version is available"
      footer={
        <>
          <Button color="inherit" onClick={() => setDismissed(true)}>
            Later
          </Button>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <RocketLaunchOutlined sx={(t) => ({ color: t.tokens.semantic.info, flexShrink: 0 })} />
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          Reload to get the latest updates. Anything you have typed but not sent will be lost, so finish what you are
          in the middle of first.
        </Typography>
      </Box>
    </Modal>
  );
}
